import {
  BillingInterval,
  EntryPoint,
  PaymentMethodType,
  PaymentMethodWalletType,
  TierLabel,
} from "@memento/types/subscription";
import { Platform } from "@memento/types/platform";
import { Card, SignUpLocation } from "@memento/types/card";
import { Feature } from "@memento/library/feature-access";
import constants from "src/constants";
import { getPlatform } from "./browser-lib";
import { ProviderNameHuman } from "./user-lib";
import { Stage, getStage } from "src/config";
import { User } from "./user-lib";
import { useAuth } from "src/hooks/useAuth";
import { useSubscription } from "src/hooks/useSubscription";
import { useCallback, useEffect, useState } from "react";
import { isBrowser } from "src/libs/nextLib";
import { formatDate } from "date-fns";

type AnalyticsEventName =
  | "BillingPeriodToggled"
  | "CheckoutCancelled"
  | "CoOrganizerAdded"
  | "CompilationDownloaded"
  | "FeatureGateLearnMoreClicked"
  | "FeatureGateSendContributors"
  | "FeatureGateViewed"
  | "GroupVideoDeleted"
  | "GroupVideoPublished"
  | "GroupVideoSentToContributors"
  | "GroupVideoSentToRecipient"
  | "GroupVideoShared"
  | "GroupVideoStarted"
  | "JoinGroupVideoClicked"
  // the user has added all contributors and is ready to send invites
  | "InvitesDoneAdding"
  | "InviteResent"
  | "InvitesResent"
  | "InvitesSent"
  | "LoginWithEmail"
  // The user selects the tier and billing interval on first page following the
  // upgrade page
  | "MembershipIntervalSelected"
  // The user selects a subscription on the upgrade page
  | "MembershipSelected"
  | "NewAccountCreatedApple"
  | "NewAccountCreatedEmail"
  | "NewAccountCreatedFacebook"
  // e.g. when the user gets the chance to "Remove Ads" when creating a group
  // video and responds
  | "OfferInterstitialAction"
  // The user sees the upgrade page, previously had a subscription, and can
  // renew it.
  | "OfferViewedRenew"
  // The user sees the upgrade page and can subscribe for the first time.
  | "OfferViewedSubscribe"
  // The user sees the upgrade page is offered a free trial.
  | "OfferViewedTrial"
  // The user sees the upgrade page, already has an active subscription, and can
  // upgrade.
  | "OfferViewedUpgrade"
  | "PhotoClipSubmitted"
  | "ReactionDiscarded"
  | "ReactionPublished"
  | "ReactionSentToContributors"
  | "ReactionSubmitted"
  | "ReactionWatched"
  | "RecordClicked"
  | "RenderStarted"
  | "UploadClicked"
  // The visitor's bucketed variant on a SEM landing-page entry path is shown
  // to them (fired once per assignment, from the destination page)
  | "SemVariantView"
  | "SubscriptionCancelled"
  | "SubscriptionUpgraded"
  | "TrialCancelled"
  | "TrialConversion"
  | "TrialStarted"
  | "UpgradeCallToActionClicked"
  | "UpgradeCancelled"
  | "EditorClipSettingsDone"
  | "EditorSectionDone"
  | "VideoClipSubmitted"
  | "ViewNewGroupVideoPage"
  | "ViewGroupVideoDashboardPage";

// Event names, including legacy events, because changing event names breaks
// historical data in Google Analytics
const analyticsEventToDataLayerEvent: Record<
  AnalyticsEventName,
  string | string[]
> = {
  BillingPeriodToggled: "memento-billing-period-toggled",
  EditorClipSettingsDone: "memento-editor-clip-settings-done",
  EditorSectionDone: "memento-editor-section-done",
  CheckoutCancelled: "memento-checkout-cancelled",
  CoOrganizerAdded: "memento-co-organizer-added",
  CompilationDownloaded: "memento-compilation-downloaded",
  FeatureGateLearnMoreClicked: "memento-feature-gate-clicked",
  FeatureGateSendContributors: "memento-feature-gate-send-contributors",
  FeatureGateViewed: "memento-feature-gate-viewed",
  GroupVideoDeleted: ["memento-group-video-deleted", "vidhug-hug-deleted"],
  GroupVideoPublished: "memento-group-video-published",
  GroupVideoSentToContributors: "memento-group-video-sent-contributors",
  GroupVideoSentToRecipient: "memento-group-video-sent-recipient",
  GroupVideoShared: "memento-group-video-shared",
  GroupVideoStarted: ["memento-group-video-started", "vidhug-hug-created"],
  JoinGroupVideoClicked: "memento-join-group-video-clicked",
  InviteResent: "memento-invite-resent",
  InvitesDoneAdding: "memento-invites-done-adding",
  InvitesResent: "memento-invites-resent",
  InvitesSent: "memento-invites-sent",
  LoginWithEmail: ["memento-login-email", "vidhug-email-login"],
  MembershipIntervalSelected: "memento-membership-interval-selected",
  MembershipSelected: "memento-membership-selected",
  NewAccountCreatedApple: ["memento-account-created", "memento-signup-apple"],
  NewAccountCreatedEmail: [
    "memento-account-created",
    "memento-signup-email",
    "vidhug-signup",
  ],
  NewAccountCreatedFacebook: [
    "memento-account-created",
    "memento-signup-facebook",
    "vidhug-fb-signup",
  ],
  OfferInterstitialAction: "memento-offer-interstitial-action",
  OfferViewedRenew: "memento-offer-viewed-renew",
  OfferViewedSubscribe: "memento-offer-viewed-subscribe",
  OfferViewedTrial: "memento-offer-viewed-trial",
  OfferViewedUpgrade: "memento-offer-viewed-upgrade",
  PhotoClipSubmitted: "memento-photo-clip-submitted",
  ReactionDiscarded: "memento-reaction-discarded",
  ReactionPublished: "memento-reaction-published",
  ReactionSentToContributors: "memento-reaction-sent-contributors",
  ReactionSubmitted: "memento-reaction-submitted",
  ReactionWatched: "memento-reaction-watched",
  RecordClicked: "memento-record-clicked",
  RenderStarted: "memento-render-started",
  UploadClicked: "memento-upload-clicked",
  SemVariantView: "memento-sem-variant-view",
  SubscriptionCancelled: "memento-subscription-cancelled",
  SubscriptionUpgraded: "memento-subscription-upgraded",
  TrialCancelled: "memento-trial-cancelled",
  TrialConversion: "memento-trial-conversion",
  TrialStarted: "memento-trial-started",
  UpgradeCallToActionClicked: "memento-upgrade-cta-clicked",
  UpgradeCancelled: "memento-upgrade-cancelled",
  VideoClipSubmitted: [
    "memento-video-clip-submitted",
    "vidhug-video-submitted",
  ],
  ViewNewGroupVideoPage: "memento-view-new-group-video-page",
  ViewGroupVideoDashboardPage: "memento-view-group-video-dashboard-page",
};

declare const window: Window &
  typeof globalThis & {
    dataLayer: any;
  };

type MementoProduct = "group-videos" | "shared-albums";

type Environment = "production" | "staging" | "development" | "test";

const stageToEnvironment: Record<Stage, Environment> = {
  [Stage.prod]: "production",
  [Stage.staging]: "staging",
  [Stage.test]: "test",
  [Stage.development]: "development",
};

/** Defining this using User.username to make it clear where the userId comes
 * from. */
type UserId = User["username"];

const getEnvironment = (): Environment => {
  return stageToEnvironment[getStage()];
};

type DataLayerMetadata = {
  billingInterval: BillingInterval | undefined;
  callToAction: string | undefined;
  cancellationReason: string | undefined;
  cancellationReasonExplanation: string | undefined;
  cardAge: string | undefined;
  contributorId: string | undefined;
  cardDueDate: string | undefined;
  cardHasFeatureImage: boolean | undefined;
  cardId: string | undefined;
  cardIsBirthday: boolean | undefined;
  cardOccasionId: string | undefined;
  cardName: string | undefined;
  cardThemeId: string | undefined;
  cardTitle: string | undefined;
  createdByGuest: boolean | undefined;
  hasFaceCollage: boolean | undefined;
  music: string | undefined;
  editorSection: string | undefined;
  theme: string | undefined;
  // Will always have a value for environment
  environment: Environment;
  externalProvider: ProviderNameHuman | undefined;
  feature: Feature | undefined;
  highlightedPlan: TierLabel | undefined;
  isTrial: boolean | undefined;
  // the minimum plan required to access the feature
  minPlanForFeature: TierLabel | undefined;
  numberOfContributors: number | undefined;
  numberOfInvites: number | undefined;
  numberOfPhotos: number | undefined;
  numberOfRecipients: number | undefined;
  numberOfVideos: number | undefined;
  offerInterstitial: "remove-ads" | undefined;
  paymentType: PaymentMethodType | undefined;
  paymentWalletType: PaymentMethodWalletType | undefined;
  offerInterstitialAction: "accepted" | "rejected" | undefined;
  // Will always have a value for platform
  platform: Platform;
  previousGroupVideosCreated: number | undefined;
  // We want this to be formatted as a dollar amount (not cents).
  priceAmount: number | undefined;
  priceCurrency: string | undefined;
  // Will always have a value for priceTestId
  priceTestId: number;
  // Will always have a value for product
  product: MementoProduct;
  renderCountV2: number | undefined;
  renderCountV3: number | undefined;
  renderTheme: string | undefined;
  rendererVersion: number | undefined;
  shareChannel: "email" | "facebook" | "whatsapp" | "copy" | undefined;
  signUpLocation: SignUpLocation | undefined;
  source: Feature | undefined;
  subscriptionId: string | undefined;
  subscriptionSelectedTier: TierLabel | undefined;
  tierLabel: TierLabel | undefined;
  tierVersion: number | undefined;
  userId: UserId | undefined;
  email: string | undefined;
  hashedEmail: string | undefined;
  videoProvider: string | undefined;
  experimentId: string | undefined;
  variantId: string | undefined;
  experimentName: string | undefined;
  variantName: string | undefined;
};

/** DataLayerMetadata except every prop is required. Just a convenience to get
 * the type of each prop without having to exclude undefined. */
type RequiredDataLayerMetadata = {
  [K in keyof DataLayerMetadata]: Exclude<DataLayerMetadata[K], undefined>;
};

/**
 * If a variable is set in a data layer event and another event is sent before
 * the page reloads, that previously set variable will appear in the second
 * event, unless if it flushed by explicitly setting it to undefined.
 */
function createDataLayerMetaDataFlush(
  data?: Partial<DataLayerMetadata>
): DataLayerMetadata {
  const defaultValues: DataLayerMetadata = {
    billingInterval: undefined,
    callToAction: undefined,
    cancellationReason: undefined,
    cancellationReasonExplanation: undefined,
    cardAge: undefined,
    contributorId: undefined,
    cardDueDate: undefined,
    cardHasFeatureImage: undefined,
    cardId: undefined,
    cardIsBirthday: undefined,
    cardOccasionId: undefined,
    cardName: undefined,
    cardThemeId: undefined,
    cardTitle: undefined,
    createdByGuest: undefined,
    // environment will never change in a session
    hasFaceCollage: undefined,
    music: undefined,
    editorSection: undefined,
    theme: undefined,
    environment: getEnvironment(),
    externalProvider: undefined,
    feature: undefined,
    highlightedPlan: undefined,
    isTrial: undefined,
    minPlanForFeature: undefined,
    numberOfContributors: undefined,
    numberOfInvites: undefined,
    numberOfPhotos: undefined,
    numberOfRecipients: undefined,
    numberOfVideos: undefined,
    offerInterstitial: undefined,
    offerInterstitialAction: undefined,
    // platform will never change in a session
    platform: getPlatform(),
    previousGroupVideosCreated: undefined,
    priceAmount: undefined,
    priceCurrency: undefined,
    // Set to 0 until we implement price testing
    priceTestId: 0,
    // The default value for this codebase. Could be different on Shared
    // Albums specific pages.
    product: "group-videos",
    renderCountV2: undefined,
    renderCountV3: undefined,
    renderTheme: undefined,
    rendererVersion: undefined,
    shareChannel: undefined,
    subscriptionId: undefined,
    subscriptionSelectedTier: undefined,
    signUpLocation: undefined,
    paymentType: undefined,
    paymentWalletType: undefined,
    source: undefined,
    tierLabel: undefined,
    tierVersion: undefined,
    userId: undefined,
    email: undefined,
    hashedEmail: undefined,
    videoProvider: undefined,
    experimentId: undefined,
    variantId: undefined,
    experimentName: undefined,
    variantName: undefined,
  };

  return { ...defaultValues, ...data };
}

// NOTE: If any of these types defines `data?: undefined`, it's just there to
// make it easier to deal with the type (we don't need to narrow the type to get
// data)
type AnalyticsEvent =
  | {
      event: "BillingPeriodToggled";
      data: { billingInterval: RequiredDataLayerMetadata["billingInterval"] };
    }
  | { event: "CheckoutCancelled"; data?: undefined }
  | {
      event: "CoOrganizerAdded";
      data: { cardId: RequiredDataLayerMetadata["cardId"] };
    }
  | {
      event: "CompilationDownloaded";
      data: {
        cardId: RequiredDataLayerMetadata["cardId"];
        numberOfVideos: RequiredDataLayerMetadata["numberOfVideos"];
        numberOfPhotos: RequiredDataLayerMetadata["numberOfPhotos"];
        numberOfContributors: RequiredDataLayerMetadata["numberOfContributors"];
        numberOfInvites: RequiredDataLayerMetadata["numberOfInvites"];
      };
    }
  | {
      event: "FeatureGateLearnMoreClicked";
      data: { feature: RequiredDataLayerMetadata["feature"] };
    }
  | {
      event: "GroupVideoDeleted";
      data?: undefined;
    }
  | {
      event: "JoinGroupVideoClicked";
      data: {
        cardId: RequiredDataLayerMetadata["cardId"];
        contributorId: RequiredDataLayerMetadata["contributorId"] | undefined;
      };
    }
  | {
      event: "GroupVideoPublished";
      data: {
        cardDueDate: RequiredDataLayerMetadata["cardDueDate"];
        cardId: RequiredDataLayerMetadata["cardId"];
        cardOccasionId: RequiredDataLayerMetadata["cardOccasionId"] | undefined;
        cardThemeId: RequiredDataLayerMetadata["cardThemeId"] | undefined;
        cardTitle: RequiredDataLayerMetadata["cardTitle"];
        numberOfContributors: RequiredDataLayerMetadata["numberOfContributors"];
      };
    }
  | {
      event: "GroupVideoSentToContributors";
      data: {
        cardHasFeatureImage: RequiredDataLayerMetadata["cardHasFeatureImage"];
        cardOccasionId: RequiredDataLayerMetadata["cardOccasionId"] | undefined;
        cardTitle: RequiredDataLayerMetadata["cardTitle"];
        cardThemeId: RequiredDataLayerMetadata["cardThemeId"] | undefined;
        numberOfPhotos: RequiredDataLayerMetadata["numberOfPhotos"] | undefined;
        numberOfRecipients: RequiredDataLayerMetadata["numberOfRecipients"];
        numberOfVideos: RequiredDataLayerMetadata["numberOfVideos"] | undefined;
      };
    }
  | {
      event: "GroupVideoSentToRecipient";
      data: {
        cardId: RequiredDataLayerMetadata["cardId"];
        numberOfContributors: RequiredDataLayerMetadata["numberOfContributors"];
        numberOfInvites: RequiredDataLayerMetadata["numberOfInvites"];
        numberOfPhotos: RequiredDataLayerMetadata["numberOfPhotos"];
        numberOfVideos: RequiredDataLayerMetadata["numberOfVideos"];
        shareChannel: RequiredDataLayerMetadata["shareChannel"];
      };
    }
  | {
      event: "GroupVideoShared";
      data: {
        cardId: RequiredDataLayerMetadata["cardId"] | undefined;
        shareChannel: RequiredDataLayerMetadata["shareChannel"];
      };
    }
  | {
      event: "GroupVideoStarted";
      data: {
        cardAge: RequiredDataLayerMetadata["cardAge"] | undefined;
        cardDueDate: RequiredDataLayerMetadata["cardDueDate"] | undefined;
        cardHasFeatureImage: RequiredDataLayerMetadata["cardHasFeatureImage"];
        cardId: RequiredDataLayerMetadata["cardId"];
        cardIsBirthday: RequiredDataLayerMetadata["cardIsBirthday"];
        cardOccasionId: RequiredDataLayerMetadata["cardOccasionId"];
        cardName: RequiredDataLayerMetadata["cardName"] | undefined;
        cardTitle: RequiredDataLayerMetadata["cardTitle"];
        cardThemeId: RequiredDataLayerMetadata["cardThemeId"];
        createdByGuest: RequiredDataLayerMetadata["createdByGuest"];
      };
    }
  | {
      event: "InvitesDoneAdding";
      data: {
        cardId: RequiredDataLayerMetadata["cardId"];
        cardDueDate: RequiredDataLayerMetadata["cardDueDate"] | undefined;
        cardOccasionId: RequiredDataLayerMetadata["cardOccasionId"] | undefined;
        numberOfInvites: RequiredDataLayerMetadata["numberOfInvites"];
      };
    }
  | {
      event: "InviteResent";
      data: {
        cardId: RequiredDataLayerMetadata["cardId"];
        numberOfInvites: RequiredDataLayerMetadata["numberOfInvites"];
      };
    }
  | {
      event: "InvitesResent";
      data: {
        cardId: RequiredDataLayerMetadata["cardId"];
        numberOfInvites: RequiredDataLayerMetadata["numberOfInvites"];
      };
    }
  | {
      event: "InvitesSent";
      data: {
        cardId: RequiredDataLayerMetadata["cardId"];
        cardDueDate: RequiredDataLayerMetadata["cardDueDate"] | undefined;
        cardOccasionId: RequiredDataLayerMetadata["cardOccasionId"] | undefined;
        numberOfInvites: RequiredDataLayerMetadata["numberOfInvites"];
      };
    }
  | { event: "LoginWithEmail"; data?: undefined }
  | {
      event: "MembershipIntervalSelected";
      data: {
        billingInterval: RequiredDataLayerMetadata["billingInterval"];
        priceAmount: RequiredDataLayerMetadata["priceAmount"];
        priceCurrency: RequiredDataLayerMetadata["priceCurrency"];
        subscriptionSelectedTier: RequiredDataLayerMetadata["subscriptionSelectedTier"];
        source: RequiredDataLayerMetadata["source"] | undefined;
      };
    }
  | {
      event: "MembershipSelected";
      data: {
        priceAmount: RequiredDataLayerMetadata["priceAmount"];
        priceCurrency: RequiredDataLayerMetadata["priceCurrency"];
        subscriptionSelectedTier: RequiredDataLayerMetadata["subscriptionSelectedTier"];
        feature: RequiredDataLayerMetadata["feature"] | undefined;
        minPlanForFeature:
          | RequiredDataLayerMetadata["minPlanForFeature"]
          | undefined;
      };
    }
  | {
      event: "NewAccountCreatedApple";
      data: {
        signUpLocation?: RequiredDataLayerMetadata["signUpLocation"];
        externalProvider: "Apple";
      };
    }
  | {
      event: "NewAccountCreatedEmail";
      data: {
        signUpLocation?: RequiredDataLayerMetadata["signUpLocation"];
      };
    }
  | {
      event: "NewAccountCreatedFacebook";
      data: {
        signUpLocation?: RequiredDataLayerMetadata["signUpLocation"];
        externalProvider: "Facebook";
      };
    }
  | {
      event: "OfferInterstitialAction";
      data: {
        offerInterstitial: RequiredDataLayerMetadata["offerInterstitial"];
        offerInterstitialAction: RequiredDataLayerMetadata["offerInterstitialAction"];
      };
    }
  | {
      event: "OfferViewedRenew";
      data: {
        feature: RequiredDataLayerMetadata["feature"] | undefined;
        minPlanForFeature:
          | RequiredDataLayerMetadata["minPlanForFeature"]
          | undefined;
      };
    }
  | {
      event: "OfferViewedSubscribe";
      data: {
        feature: RequiredDataLayerMetadata["feature"] | undefined;
        minPlanForFeature:
          | RequiredDataLayerMetadata["minPlanForFeature"]
          | undefined;
      };
    }
  | {
      event: "OfferViewedTrial";

      data: {
        feature: RequiredDataLayerMetadata["feature"] | undefined;
        minPlanForFeature:
          | RequiredDataLayerMetadata["minPlanForFeature"]
          | undefined;
      };
    }
  | {
      event: "OfferViewedUpgrade";
      data: {
        feature: RequiredDataLayerMetadata["feature"] | undefined;
        minPlanForFeature:
          | RequiredDataLayerMetadata["minPlanForFeature"]
          | undefined;
      };
    }
  | {
      event: "PhotoClipSubmitted";
      data: {
        cardId: RequiredDataLayerMetadata["cardId"];
        contributorId: RequiredDataLayerMetadata["contributorId"] | undefined;
        uploaderType: "organizer" | "contributor";
      };
    }
  | { event: "ReactionDiscarded"; data?: undefined }
  | { event: "ReactionPublished"; data?: undefined }
  | {
      event: "ReactionSentToContributors";
      data: {
        numberOfRecipients: RequiredDataLayerMetadata["numberOfRecipients"];
      };
    }
  | { event: "ReactionSubmitted"; data?: undefined }
  | { event: "ReactionWatched"; data?: undefined }
  | {
      event: "RecordClicked";
      data: {
        cardId: RequiredDataLayerMetadata["cardId"];
        contributorId: RequiredDataLayerMetadata["contributorId"] | undefined;
      };
    }
  | {
      event: "RenderStarted";
      data: {
        cardId: RequiredDataLayerMetadata["cardId"] | undefined;
        rendererVersion:
          | RequiredDataLayerMetadata["rendererVersion"]
          | undefined;
        renderTheme: RequiredDataLayerMetadata["renderTheme"] | undefined;
        renderCountV2: RequiredDataLayerMetadata["renderCountV2"];
        renderCountV3: RequiredDataLayerMetadata["renderCountV3"];
        numberOfVideos: RequiredDataLayerMetadata["numberOfVideos"];
        numberOfPhotos: RequiredDataLayerMetadata["numberOfPhotos"];
        numberOfContributors: RequiredDataLayerMetadata["numberOfContributors"];
        numberOfInvites: RequiredDataLayerMetadata["numberOfInvites"];
      };
    }
  | {
      event: "SubscriptionCancelled";
      data: {
        tierLabel: RequiredDataLayerMetadata["tierLabel"];
        tierVersion: RequiredDataLayerMetadata["tierVersion"];
        priceAmount: RequiredDataLayerMetadata["priceAmount"] | undefined;
        priceCurrency: RequiredDataLayerMetadata["priceCurrency"] | undefined;
        billingInterval:
          | RequiredDataLayerMetadata["billingInterval"]
          | undefined;
        cancellationReason: RequiredDataLayerMetadata["cancellationReason"];
        cancellationReasonExplanation: RequiredDataLayerMetadata["cancellationReasonExplanation"];
      };
    }
  | {
      event: "SubscriptionUpgraded";
      data: {
        isTrial: RequiredDataLayerMetadata["isTrial"] | undefined;
        tierLabel: RequiredDataLayerMetadata["tierLabel"] | undefined;
        tierVersion: RequiredDataLayerMetadata["tierVersion"] | undefined;
        priceAmount: RequiredDataLayerMetadata["priceAmount"] | undefined;
        priceCurrency: RequiredDataLayerMetadata["priceCurrency"] | undefined;
        billingInterval:
          | RequiredDataLayerMetadata["billingInterval"]
          | undefined;
        subscriptionId: RequiredDataLayerMetadata["subscriptionId"] | undefined;
        paymentType: RequiredDataLayerMetadata["paymentType"] | undefined;
        paymentWalletType:
          | RequiredDataLayerMetadata["paymentWalletType"]
          | undefined;
        source: RequiredDataLayerMetadata["source"] | undefined;
        email?: RequiredDataLayerMetadata["email"] | undefined;
        hashedEmail?: RequiredDataLayerMetadata["hashedEmail"] | undefined;
        experimentId?: RequiredDataLayerMetadata["experimentId"] | undefined;
        variantId?: RequiredDataLayerMetadata["variantId"] | undefined;
        experimentName?:
          | RequiredDataLayerMetadata["experimentName"]
          | undefined;
        variantName?: RequiredDataLayerMetadata["variantName"] | undefined;
      };
    }
  | {
      event: "TrialCancelled";
      data: {
        tierLabel: RequiredDataLayerMetadata["tierLabel"];
        tierVersion: RequiredDataLayerMetadata["tierVersion"];
        priceAmount: RequiredDataLayerMetadata["priceAmount"] | undefined;
        priceCurrency: RequiredDataLayerMetadata["priceCurrency"] | undefined;
        billingInterval:
          | RequiredDataLayerMetadata["billingInterval"]
          | undefined;
        cancellationReason: RequiredDataLayerMetadata["cancellationReason"];
        cancellationReasonExplanation: RequiredDataLayerMetadata["cancellationReasonExplanation"];
      };
    }
  | {
      event: "TrialConversion";
      data: {
        tierLabel: RequiredDataLayerMetadata["tierLabel"] | undefined;
        tierVersion: RequiredDataLayerMetadata["tierVersion"] | undefined;
        priceAmount: RequiredDataLayerMetadata["priceAmount"] | undefined;
        priceCurrency: RequiredDataLayerMetadata["priceCurrency"] | undefined;
        billingInterval:
          | RequiredDataLayerMetadata["billingInterval"]
          | undefined;
        subscriptionId: RequiredDataLayerMetadata["subscriptionId"] | undefined;
        paymentType: RequiredDataLayerMetadata["paymentType"] | undefined;
        paymentWalletType:
          | RequiredDataLayerMetadata["paymentWalletType"]
          | undefined;
        hashedEmail?: RequiredDataLayerMetadata["hashedEmail"] | undefined;
      };
    }
  | {
      event: "TrialStarted";
      data: {
        isTrial: RequiredDataLayerMetadata["isTrial"] | undefined;
        tierLabel: RequiredDataLayerMetadata["tierLabel"] | undefined;
        tierVersion: RequiredDataLayerMetadata["tierVersion"] | undefined;
        priceAmount: RequiredDataLayerMetadata["priceAmount"] | undefined;
        priceCurrency: RequiredDataLayerMetadata["priceCurrency"] | undefined;
        billingInterval:
          | RequiredDataLayerMetadata["billingInterval"]
          | undefined;
        subscriptionId: RequiredDataLayerMetadata["subscriptionId"] | undefined;
        paymentType: RequiredDataLayerMetadata["paymentType"] | undefined;
        paymentWalletType:
          | RequiredDataLayerMetadata["paymentWalletType"]
          | undefined;
        source: RequiredDataLayerMetadata["source"] | undefined;
        email?: RequiredDataLayerMetadata["email"] | undefined;
        hashedEmail?: RequiredDataLayerMetadata["hashedEmail"] | undefined;
        experimentId?: RequiredDataLayerMetadata["experimentId"] | undefined;
        variantId?: RequiredDataLayerMetadata["variantId"] | undefined;
        experimentName?:
          | RequiredDataLayerMetadata["experimentName"]
          | undefined;
        variantName?: RequiredDataLayerMetadata["variantName"] | undefined;
      };
    }
  | {
      event: "UpgradeCallToActionClicked";
      data: {
        tierLabel: RequiredDataLayerMetadata["tierLabel"];
        billingInterval: RequiredDataLayerMetadata["billingInterval"];
        priceAmount: RequiredDataLayerMetadata["priceAmount"];
        priceCurrency: RequiredDataLayerMetadata["priceCurrency"];
        callToAction: RequiredDataLayerMetadata["callToAction"] | undefined;
        highlightedPlan:
          | RequiredDataLayerMetadata["highlightedPlan"]
          | undefined;
        source: RequiredDataLayerMetadata["source"] | undefined;
      };
    }
  | {
      event: "UpgradeCancelled";
      data: {
        tierLabel: RequiredDataLayerMetadata["tierLabel"];
        tierVersion: RequiredDataLayerMetadata["tierVersion"];
        billingInterval: RequiredDataLayerMetadata["billingInterval"];
        priceAmount: RequiredDataLayerMetadata["priceAmount"];
        priceCurrency: RequiredDataLayerMetadata["priceCurrency"];
      };
    }
  | {
      event: "UploadClicked";
      data: {
        cardId: RequiredDataLayerMetadata["cardId"];
        contributorId: RequiredDataLayerMetadata["contributorId"] | undefined;
      };
    }
  | {
      event: "VideoClipSubmitted";
      data: {
        cardId: RequiredDataLayerMetadata["cardId"];
        videoProvider: RequiredDataLayerMetadata["videoProvider"];
        contributorId: RequiredDataLayerMetadata["contributorId"] | undefined;
        uploaderType: "organizer" | "contributor";
      };
    }
  | {
      event: "EditorClipSettingsDone";
      data: {
        cardId: RequiredDataLayerMetadata["cardId"];
      };
    }
  | {
      event: "EditorSectionDone";
      data: {
        cardId: RequiredDataLayerMetadata["cardId"];
        editorSection: RequiredDataLayerMetadata["editorSection"];
        theme?: RequiredDataLayerMetadata["theme"];
        music?: RequiredDataLayerMetadata["music"];
        hasFaceCollage?: RequiredDataLayerMetadata["hasFaceCollage"];
      };
    }
  | { event: "ViewNewGroupVideoPage"; data?: undefined }
  | {
      event: "ViewGroupVideoDashboardPage";
      data: {
        cardId: RequiredDataLayerMetadata["cardId"];
      };
    }
  | {
      event: "SemVariantView";
      data: {
        experimentId: RequiredDataLayerMetadata["experimentId"];
        variantId: RequiredDataLayerMetadata["variantId"];
        experimentName?:
          | RequiredDataLayerMetadata["experimentName"]
          | undefined;
        variantName?: RequiredDataLayerMetadata["variantName"] | undefined;
      };
    };

/** Props that should exist on every event. Even if we can't get a value, we
have to explicitly pass undefined. */
type CommonAnalyticsEventData = {
  userId: RequiredDataLayerMetadata["userId"] | undefined;
  tierLabel: RequiredDataLayerMetadata["tierLabel"] | undefined;
  hashedEmail?: RequiredDataLayerMetadata["hashedEmail"] | undefined;
  // product is almost always going to be group-videos, so we won't require it
  product?: RequiredDataLayerMetadata["product"];
};

type SendAnalyticsEventProps = AnalyticsEvent & CommonAnalyticsEventData;

export function sendAnalyticsEvent({
  event,
  userId,
  tierLabel,
  hashedEmail,
  product,
  data,
}: SendAnalyticsEventProps) {
  const eventDataWithUserId = {
    // include tierLabel first because some events required tierLabel in this
    // data. This way we won't override it with e.g. undefined
    tierLabel,
    ...(product && { product }),
    ...data,
    userId,
    hashedEmail,
  };

  const dataLayerEvent = analyticsEventToDataLayerEvent[event];

  try {
    if (typeof dataLayerEvent === "string") {
      sendDataLayerEvent(dataLayerEvent, eventDataWithUserId);
    } else {
      // event is an array of event names
      dataLayerEvent.forEach((eventName) =>
        sendDataLayerEvent(eventName, eventDataWithUserId)
      );
    }
  } catch (e) {
    // Analytics failure shouldn't cause problems
    console.log(e);
  }
}

/** Hashes an email for use in GA4 Enhanced Conversions (sha256_email_address). */
export async function hashEmailForGA(email: string): Promise<string> {
  const normalized = email.trim().toLowerCase();
  const msgBuffer = new TextEncoder().encode(
    normalized
  ) as Uint8Array<ArrayBuffer>;
  const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/** Convenience hook for sending analytics events. It automatically passes props
 * that need to be fetched from other hooks. */
export function useSendAnalyticsEvent() {
  const { user } = useAuth();
  const { subscription } = useSubscription();

  const userId = user?.username;
  const tierLabel = subscription?.tier?.label;
  const email = user?.email;

  const [hashedEmail, setHashedEmail] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (email) {
      hashEmailForGA(email).then(setHashedEmail);
    } else {
      setHashedEmail(undefined);
    }
  }, [email]);

  const thisSendAnalyticsEvent = useCallback(
    (
      event: AnalyticsEvent & {
        product?: CommonAnalyticsEventData["product"];
      }
    ) => {
      sendAnalyticsEvent({
        userId,
        tierLabel,
        hashedEmail,
        ...event,
      });
    },
    [userId, tierLabel, hashedEmail]
  );

  return {
    sendAnalyticsEvent: thisSendAnalyticsEvent,
    email,
    tierLabel,
    userId,
  };
}

export function sendPublishedEvent(
  card: Card | undefined,
  eventArgs: Omit<
    Extract<SendAnalyticsEventProps, { event: "GroupVideoPublished" }>,
    "event"
  >
) {
  if (
    card &&
    card.createdAt >= constants.minimumPublishCreatedAt &&
    !card.publishedAt
  ) {
    sendAnalyticsEvent({
      event: "GroupVideoPublished",
      ...eventArgs,
    });
  }
}

function sendDataLayerEvent(
  event: string,
  eventData: Partial<DataLayerMetadata>
) {
  const dataToSend = createDataLayerMetaDataFlush(eventData);

  if (isBrowser()) {
    window?.dataLayer?.push({ event, ...dataToSend });
  }
}

const entryPointToProduct: Record<EntryPoint, MementoProduct> = {
  "group-videos": "group-videos",
  "shared-albums": "shared-albums",
};

export function getProduct(entryPoint: EntryPoint) {
  return entryPointToProduct[entryPoint];
}

export function guardAge(age: string): string {
  if (age === constants.birthdayAge.unselected) {
    return "";
  } else if (age === constants.birthdayAge.notSure) {
    return "Not sure";
  } else if (age === constants.birthdayAge.preferNotToSay) {
    return "Prefer not to say";
  } else {
    return age;
  }
}

export function formatDueDate(dueDate: Date | string | number): string {
  return formatDate(dueDate, "MM/dd/yyyy");
}

export function getDollarPriceFromCents(priceCents: number): number {
  return priceCents / 100;
}
